import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Loader2, Send, RefreshCw, CheckCircle2, Clock, ExternalLink,
  Mail, FileCheck, AlertTriangle, ClipboardCheck, FileWarning,
} from 'lucide-react';
import RequestAdditionalDocsDialog from './RequestAdditionalDocsDialog';

const STATUT_META = {
  prospect: {
    label: 'Candidat détecté',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
    hint: "Candidature reçue via IMAP. Aucun email n'a encore été envoyé — cliquez sur « Envoyer formulaire » pour lancer l'onboarding.",
  },
  en_attente_docs: {
    label: 'Formulaire envoyé',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    hint: "Le candidat a reçu le lien du formulaire. En attente de sa réponse.",
  },
  en_examen: {
    label: 'En examen par notre équipe',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
    hint: "Le candidat a soumis son dossier. Vérifiez ses tarifs, ses documents légaux, puis cliquez sur « Valider le référencement » pour l'activer.",
  },
  actif: {
    label: 'Actif',
    color: 'bg-green-100 text-green-800 border-green-200',
    dot: 'bg-green-500',
    hint: 'Sous-traitant validé et référencé dans votre réseau.',
  },
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return iso; }
}

function timeAgo(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "à l'instant";
    if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
    if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
    return `il y a ${Math.floor(s / 86400)} j`;
  } catch (_e) { return null; }
}

/**
 * Panneau d'état de candidature pour un sous-traitant.
 * Affiché uniquement quand `statut_recrutement !== 'actif'`.
 * Actions : Envoyer/renvoyer formulaire · Relancer · Ouvrir formulaire · Valider.
 */
export default function CandidaturePanel({ stId, onStatusChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'send' | 'reminder' | 'activate'
  const [reqDocsOpen, setReqDocsOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stId]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sous-traitants/${stId}/candidature`);
      setData(r.data);
    } catch (e) {
      console.error('Load candidature failed', e);
    } finally { setLoading(false); }
  };

  const doSendFormulaire = async (dryRun) => {
    setAction('send');
    try {
      const r = await api.post(`/sous-traitants/${stId}/send-onboarding`, { dry_run: dryRun });
      if (dryRun) {
        try { await navigator.clipboard.writeText(r.data.form_url); } catch (_e) {}
        toast.success('Simulation OK · Lien copié dans le presse-papier', { duration: 6000 });
      } else {
        toast.success(`Email envoyé à ${r.data.sent_to}${r.data.override_used ? ' (mode test)' : ''}`, { duration: 5000 });
      }
      await load();
      onStatusChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally { setAction(null); }
  };

  const doReminder = async () => {
    if (!window.confirm('Envoyer une relance maintenant au candidat ?')) return;
    setAction('reminder');
    try {
      const r = await api.post(`/sous-traitants/${stId}/candidature/send-reminder`);
      toast.success(`Relance ${r.data.reminders_sent} envoyée à ${r.data.sent_to}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally { setAction(null); }
  };

  const doActivate = async () => {
    if (!window.confirm(
      "Confirmer l'activation ?\n\n" +
      "Le candidat deviendra un sous-traitant à part entière, disponible pour affectation sur les chantiers. Assurez-vous d'avoir vérifié ses tarifs et ses documents légaux."
    )) return;
    setAction('activate');
    try {
      await api.post(`/sous-traitants/${stId}/candidature/activate`);
      toast.success('Référencement activé — bienvenue dans le réseau ! 🎉');
      await load();
      onStatusChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally { setAction(null); }
  };

  if (loading) return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
    </div>
  );
  if (!data) return null;
  // Ne rien afficher si actif ET pas de request historique (fiche créée manuellement)
  if (data.statut_recrutement === 'actif' && !data.onboarding_request) return null;

  const meta = STATUT_META[data.statut_recrutement] || STATUT_META.prospect;
  const req = data.onboarding_request;
  const hasRequest = !!req;
  const isSubmitted = hasRequest && !!req.submitted_at;
  const canSendReminder = hasRequest && !isSubmitted;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-testid="candidature-panel">
      {/* Header status */}
      <div className={`px-5 py-4 border-b ${meta.color} border-current/20 flex items-start gap-3`}>
        <span className={`w-2.5 h-2.5 rounded-full ${meta.dot} animate-pulse mt-1.5 flex-shrink-0`}></span>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-semibold opacity-80">Statut candidature</div>
          <div className="text-lg font-bold mt-0.5">{meta.label}</div>
          <div className="text-sm mt-1 opacity-90">{meta.hint}</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-5 space-y-3">
        {data.source && (
          <TimelineItem
            icon={Mail}
            label="Candidature reçue par email"
            value={
              <>
                <span className="font-medium">{data.source.email_subject}</span><br />
                <span className="text-xs text-slate-500">{data.source.email_from} · via {data.source.mailbox}</span>
              </>
            }
          />
        )}
        {hasRequest && (
          <>
            <TimelineItem
              icon={Send}
              label="Formulaire d'onboarding envoyé"
              value={
                <>
                  {fmtDate(req.created_at)} <span className="text-xs text-slate-500">({timeAgo(req.created_at)})</span>
                  {req.actual_recipient && <><br /><span className="text-xs text-slate-500">→ {req.actual_recipient}</span></>}
                </>
              }
            />
            {req.reminders_sent > 0 && (
              <TimelineItem
                icon={RefreshCw}
                label={`Relance${req.reminders_sent > 1 ? 's' : ''} (${req.reminders_sent})`}
                value={
                  <>Dernière : {fmtDate(req.last_reminder_at)} <span className="text-xs text-slate-500">({timeAgo(req.last_reminder_at)})</span></>
                }
              />
            )}
            {req.uploads?.length > 0 && (
              <TimelineItem
                icon={FileCheck}
                label={`Documents déposés (${req.uploads.length})`}
                value={
                  <ul className="space-y-0.5 mt-0.5">
                    {req.uploads.map((u, i) => (
                      <li key={i} className="text-xs text-slate-600">
                        <b>{u.doc_type}</b> · {u.filename} · {timeAgo(u.uploaded_at)}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            {isSubmitted && (
              <TimelineItem
                icon={ClipboardCheck}
                label="Dossier soumis par le candidat"
                value={<>{fmtDate(req.submitted_at)} <span className="text-xs text-slate-500">({timeAgo(req.submitted_at)})</span></>}
                accent="green"
              />
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex flex-wrap gap-2" data-testid="candidature-actions">
        {!hasRequest && (
          <>
            <Button onClick={() => doSendFormulaire(false)} disabled={action === 'send' || !data.email}
              className="bg-purple-600 hover:bg-purple-700" data-testid="candidature-send-btn">
              {action === 'send' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer le formulaire
            </Button>
            <Button variant="outline" onClick={() => doSendFormulaire(true)} disabled={action === 'send'}>
              Simuler (générer le lien)
            </Button>
          </>
        )}
        {hasRequest && !isSubmitted && (
          <>
            <Button onClick={doReminder} disabled={action === 'reminder'} data-testid="candidature-remind-btn">
              {action === 'reminder' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Envoyer une relance
            </Button>
            <Button variant="outline" onClick={() => doSendFormulaire(false)} disabled={action === 'send'}>
              <Send className="w-4 h-4 mr-2" />
              Renvoyer le formulaire
            </Button>
            <a href={req.form_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ouvrir le formulaire
              </Button>
            </a>
          </>
        )}
        {isSubmitted && data.statut_recrutement !== 'actif' && (
          <Button onClick={doActivate} disabled={action === 'activate'}
            className="bg-green-600 hover:bg-green-700" data-testid="candidature-activate-btn">
            {action === 'activate' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Valider le référencement
          </Button>
        )}
        {isSubmitted && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setReqDocsOpen(true)}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="candidature-request-docs-btn"
            title="Redemander un ou plusieurs documents non conformes avec un message personnalisé"
          >
            <FileWarning className="w-4 h-4 mr-2" />
            Redemander des documents
          </Button>
        )}
        {!isSubmitted && data.statut_recrutement !== 'actif' && (
          <Button variant="outline" onClick={doActivate} disabled={action === 'activate'}
            className="ml-auto border-green-300 text-green-700 hover:bg-green-50">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Activer sans attendre
          </Button>
        )}
      </div>

      {!data.email && (
        <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Aucun email n'est renseigné pour ce candidat. Ajoutez son email pour pouvoir envoyer le formulaire.</span>
        </div>
      )}

      <RequestAdditionalDocsDialog
        open={reqDocsOpen}
        onOpenChange={setReqDocsOpen}
        stId={stId}
        stNom={data.sous_traitant_nom || data.nom || ''}
        stEmail={data.email}
        uploadedDocTypes={(data.documents_uploaded || []).map((d) => d.doc_type)}
        onSent={() => load()}
      />
    </div>
  );
}

function TimelineItem({ icon: Icon, label, value, accent }) {
  const iconColor = accent === 'green' ? 'text-green-600 bg-green-50'
    : 'text-slate-600 bg-slate-100';
  return (
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-full ${iconColor} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-medium text-slate-800">{label}</div>
        <div className="text-slate-600 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
